import { useEffect, useState } from 'react';
import { NetworkStatus } from 'apollo-client';
import type { ApolloClient, QueryOptions } from 'apollo-client';
import type { OperationVariables } from '@apollo/react-common';
import type { DocumentNode } from 'graphql';
import type { ConcreteRequest, GraphQLTaggedNode } from 'relay-runtime';
import { useApolloClient } from '@apollo/react-hooks';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { toDocumentNode } from './toDocumentNode.ts';

type QueryNoFurtherUpdateResult<TData = any> = {
	data: TData | undefined;
	error?: ApolloErrorInterop;
	loading: boolean;
	networkStatus: NetworkStatus;
};

const queryResults = new WeakMap<
	DocumentNode,
	Map<string, Promise<QueryNoFurtherUpdateResult<any>>>
>();

/**
 * This hook works like `useQuery` but it will not update the result after the initial fetch.
 * Essentially it works like fetchPolicy: "standby". See https://www.apollographql.com/docs/react/data/queries/#standby
 *
 * The difference between this hook and `useQuery` is it internally uses pure promise instead of Observable.
 * In the case a query is frequent fetched this hook performs much faster than the Apollo's useQuery.
 * If other fetchPolicy is needed, use `useQuery` instead.
 */
export function useQueryNoFurtherUpdateInterop<TData = any, TVariables = OperationVariables>(
	query: DocumentNode | GraphQLTaggedNode,
	options?: Omit<QueryOptions<TVariables>, 'query' | 'fetchPolicy'> & {
		fetchPolicy?: 'standby';
		skip?: boolean;
		client?: ApolloClient<any>;
	},
): QueryNoFurtherUpdateResult<TData | null> {
	let clientFromContext: ApolloClient<any> | undefined;
	try {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		clientFromContext = useApolloClient();
	} catch {
		// useApolloClient throws "No Apollo Client instance can be found". It is expected in some unit tests.
	}

	// technically not a hash but it's good enough since variable is an plain object.
	const variablesHash = JSON.stringify(options?.variables || {});
	const context = { ...options?.context, requestId: (query as ConcreteRequest).params?.id };
	query = toDocumentNode(query);

	const client =
		options?.client || clientFromContext || (window['GLOBAL_APOLLO_CLIENT'] as ApolloClient<any>);
	let dataFromCache: TData | null = null;
	try {
		if (!options?.skip) {
			dataFromCache = client.readQuery<TData, TVariables>({
				query,
				...options,
			});
		}
	} catch {}

	const [result, setResult] = useState<QueryNoFurtherUpdateResult<TData | null>>(() => {
		if (options?.skip) {
			return { data: null, loading: false, networkStatus: NetworkStatus.ready };
		}

		return dataFromCache
			? { data: dataFromCache, loading: false, networkStatus: NetworkStatus.ready }
			: { data: null, loading: true, networkStatus: NetworkStatus.loading };
	});

	useEffect(() => {
		if (options?.skip) return;

		// If query is previously fetched but with different variables. Re-fetch the query.
		const queryFetched = queryResults.has(query) && queryResults.get(query)!.has(variablesHash);
		if (result.loading || !queryFetched) {
			// Same query + variable should reuse the same promise to avoid going through expensive Apollo client.
			let queryResultPromise: Promise<QueryNoFurtherUpdateResult<TData>>;
			if (queryFetched) {
				queryResultPromise = queryResults.get(query)?.get(variablesHash)!;
			} else {
				queryResultPromise = client.query<TData, TVariables>({
					query,
					...options,
					context,
				});
				if (!queryResults.has(query)) {
					queryResults.set(query, new Map());
				}
				// Do not set cache when running in jest otherwise test result will be affected by other tests.
				if (!process.env.JEST_WORKER_ID) {
					queryResults.get(query)!.set(variablesHash, queryResultPromise);
				}
			}

			void queryResultPromise
				.then((result) => setResult(result))
				.catch((e): void => {
					setResult({
						...result,
						loading: false,
						networkStatus: NetworkStatus.ready,
						error: e,
					});
				});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variablesHash]);

	return result;
}
